import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Pencil, Plus, X } from "lucide-react";
import { GoogleIcon, Spinner, Sheet, Field, Input, PrimaryButton } from "@/components/peeps/ui";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { colors } from "@/lib/theme";
import logo from "@/assets/peeps-logo.png";

export function AboutContent({ content }) {
  const c = content || { heading: "What is peeps?", paragraphs: [], tagline: "" };
  return (
    <div className="font-display" style={{ color: colors.onSurface }}>
      <h2 className="font-bold text-3xl sm:text-4xl leading-tight mb-4" style={{ color: colors.onSurface }}>
        {c.heading}
      </h2>
      {(c.paragraphs || []).map((p, i) => (
        <p key={i} className="text-[15px] leading-relaxed mb-4" style={{ color: colors.onSurfaceTertiary }}>{p}</p>
      ))}
      {c.tagline && (
        <p className="text-[17px] font-bold mt-6" style={{ color: colors.brandSecondary }}>{c.tagline}</p>
      )}
    </div>
  );
}

export default function About() {
  const navigate = useNavigate();
  const { user, loading, login, signingIn } = useAuth();
  const [content, setContent] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.getAbout().then(setContent).catch(() => {}); }, []);

  const openEdit = () => {
    setDraft(JSON.parse(JSON.stringify(content || { heading: "", paragraphs: [""], tagline: "" })));
    setEditing(true);
  };
  const saveAbout = async () => {
    setSaving(true);
    try {
      const saved = await api.updateAbout({ heading: draft.heading, paragraphs: draft.paragraphs, tagline: draft.tagline });
      setContent(saved);
      setEditing(false);
      toast("About page updated");
    } catch (e) { toast(e.message || "Couldn't save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: colors.surface }}>
      <div className="max-w-2xl mx-auto px-6 py-8">
        <div className="flex items-center gap-2 mb-8">
          <button data-testid="about-back" onClick={() => (user ? navigate(-1) : navigate("/login"))} className="p-2 -ml-2">
            <ChevronLeft size={22} color={colors.onSurface} />
          </button>
          <img src={logo} alt="peeps" className="w-9 h-9 rounded-xl" />
          <span className="font-display font-bold text-[20px]" style={{ color: colors.brand }}>peeps</span>
          {user?.is_app_admin && (
            <button data-testid="about-edit-button" onClick={openEdit} className="ml-auto flex items-center gap-1.5 rounded-full px-3 py-1.5" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
              <Pencil size={13} color={colors.brandSecondary} />
              <span className="font-display font-medium text-[12.5px]" style={{ color: colors.brandSecondary }}>Edit</span>
            </button>
          )}
        </div>

        {content ? <AboutContent content={content} /> : <Spinner />}

        <button
          data-testid="about-guide-link"
          onClick={() => navigate("/how-it-works")}
          className="mt-7 font-display font-medium text-[14px] underline underline-offset-4"
          style={{ color: colors.brandSecondary }}
        >
          How peeps works →
        </button>

        {!loading && !user && (
          <button
            data-testid="about-google-login"
            onClick={() => login("/")}
            disabled={signingIn}
            className="mt-8 w-full flex items-center justify-center gap-2.5 rounded-xl py-3.5"
            style={{ background: "#fff" }}
          >
            <GoogleIcon size={18} />
            <span className="font-display font-bold text-[15px]" style={{ color: "#1a1a1a" }}>{signingIn ? "Signing in…" : "Sign in with Google"}</span>
          </button>
        )}
      </div>

      {editing && draft && (
        <Sheet visible={editing} onClose={() => setEditing(false)} title="Edit About page" testID="about-edit-sheet">
          <Field label="Heading">
            <Input testID="about-heading-input" value={draft.heading} onChange={(e) => setDraft({ ...draft, heading: e.target.value })} />
          </Field>
          <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-1.5 ml-0.5" style={{ color: colors.muted }}>Paragraphs</p>
          <div className="flex flex-col gap-2 mb-3">
            {draft.paragraphs.map((p, i) => (
              <div key={i} className="relative">
                <textarea
                  data-testid={`about-paragraph-${i}`}
                  value={p}
                  onChange={(e) => { const ps = [...draft.paragraphs]; ps[i] = e.target.value; setDraft({ ...draft, paragraphs: ps }); }}
                  rows={3}
                  className="w-full rounded-xl p-3 pr-9 font-display text-[14px] resize-none"
                  style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}`, color: colors.onSurface }}
                />
                <button onClick={() => setDraft({ ...draft, paragraphs: draft.paragraphs.filter((_, j) => j !== i) })} className="absolute top-2 right-2 p-1 rounded-full" style={{ background: colors.surfaceSecondary }}>
                  <X size={13} color={colors.muted} />
                </button>
              </div>
            ))}
            <button data-testid="about-add-paragraph" onClick={() => setDraft({ ...draft, paragraphs: [...draft.paragraphs, ""] })} className="flex items-center gap-1.5 self-start rounded-lg px-3 py-2" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
              <Plus size={14} color={colors.brandSecondary} />
              <span className="font-display font-medium text-[12.5px]" style={{ color: colors.brandSecondary }}>Add paragraph</span>
            </button>
          </div>
          <Field label="Tagline">
            <Input testID="about-tagline-input" value={draft.tagline} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} />
          </Field>
          <PrimaryButton testID="about-save-button" label="Save About page" onClick={saveAbout} disabled={!draft.heading.trim()} loading={saving} />
        </Sheet>
      )}
    </div>
  );
}
